import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { PosNote } from '../../types';

function isForbiddenError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

function replaceNote(list: PosNote[], updated: PosNote): PosNote[] {
  return list.map((n) => (n.id === updated.id ? updated : n));
}

function sortNotes(list: PosNote[]): PosNote[] {
  return [...list].sort((a, b) => {
    if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
    return (
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  });
}

async function createNote(
  posId: string,
  body: string,
  isPinned: boolean
): Promise<PosNote> {
  const res = await api.post<PosNote>(`/pos/${posId}/notes`, {
    body,
    kind: 'general',
    is_pinned: isPinned,
  });
  return res.data;
}

async function patchNote(
  noteId: string,
  body: string,
  isPinned: boolean
): Promise<PosNote> {
  const res = await api.patch<PosNote>(`/pos/notes/${noteId}`, {
    body,
    is_pinned: isPinned,
  });
  return res.data;
}

function toastMutationError(error: unknown, action: string): void {
  if (isForbiddenError(error)) {
    toast.error(`You do not have permission to ${action} this note`);
    return;
  }
  console.error(`Failed to ${action} note:`, error);
  toast.error(`Failed to ${action} note`);
}

export function usePosNotes(posId: string) {
  const [notes, setNotes] = useState<PosNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<PosNote[]>(`/pos/${posId}/notes`);
      setNotes(res.data);
    } catch (error) {
      console.error('Failed to load notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [posId]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  const addNote = useCallback(
    async (body: string, isPinned: boolean): Promise<boolean> => {
      try {
        setSubmitting(true);
        const created = await createNote(posId, body, isPinned);
        setNotes((prev) => sortNotes([created, ...prev]));
        toast.success('Note added');
        return true;
      } catch (error) {
        toastMutationError(error, 'add');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [posId]
  );

  const saveEdit = useCallback(
    async (
      noteId: string,
      body: string,
      isPinned: boolean
    ): Promise<boolean> => {
      try {
        setBusyId(noteId);
        const updated = await patchNote(noteId, body, isPinned);
        setNotes((prev) => sortNotes(replaceNote(prev, updated)));
        setEditingId(null);
        toast.success('Note updated');
        return true;
      } catch (error) {
        toastMutationError(error, 'edit');
        return false;
      } finally {
        setBusyId(null);
      }
    },
    []
  );

  const deleteNote = useCallback(
    async (noteId: string): Promise<void> => {
      try {
        setBusyId(noteId);
        await api.delete(`/pos/notes/${noteId}`);
        setNotes((prev) => prev.filter((n) => n.id !== noteId));
        setEditingId((current) => (current === noteId ? null : current));
        toast.success('Note deleted');
      } catch (error) {
        toastMutationError(error, 'delete');
      } finally {
        setBusyId(null);
      }
    },
    []
  );

  return {
    notes,
    loading,
    submitting,
    busyId,
    editingId,
    setEditingId,
    addNote,
    saveEdit,
    deleteNote,
  };
}
